import { Download, Mail } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { AccountsPayableReportContext } from '../components/reports/tabs/AccountsPayableReport'
import {
  type AppliedCashRegisterReportContext,
  CajasTab,
} from '../components/reports/tabs/CajasTab'
import {
  type AppliedCfdiReportContext,
  CfdiTab,
} from '../components/reports/tabs/CfdiTab'
import {
  type AppliedPurchaseReportContext,
  ComprasTab,
  type PurchaseReportTab,
} from '../components/reports/tabs/ComprasTab'
import {
  type AppliedSaleReturnReportContext,
  DevolucionesTab,
} from '../components/reports/tabs/DevolucionesTab'
import {
  type AppliedInventoryReportContext,
  InventarioTab,
} from '../components/reports/tabs/InventarioTab'
import {
  type AppliedCashMovementReportContext,
  MovimientosTab,
} from '../components/reports/tabs/MovimientosTab'
import {
  type AppliedOrderReportContext,
  PedidosTab,
} from '../components/reports/tabs/PedidosTab'
import {
  type AppliedSalesReportContext,
  VentasTab,
} from '../components/reports/tabs/VentasTab'
import { ExportModal } from '../components/ui/ExportModal'
import type { ExportFilters, ExportFormat } from '../components/ui/ExportModal'
import {
  SendEmailModal,
  type SendEmailPayload,
} from '../components/ui/SendEmailModal'
import { Button } from '../components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  reportsService,
  type CashRegisterReportType,
  type CfdiReportType,
  type InventoryReportType,
  type OrderReportType,
  type ReportEmailRequestResult,
  type SalesReportType,
} from '../services/reports.service'
import { useSessionStore } from '../store/auth/auth.store'

const REPORT_SECTION_AVAILABILITY = {
  devoluciones: { production: true },
  pedidos: { production: true },
  cfdi: { production: true },
  compras: { production: true },
} as const

type DevelopmentReportSection = keyof typeof REPORT_SECTION_AVAILABILITY

function isReportSectionVisible(section: DevelopmentReportSection): boolean {
  const showOnlyProductionModules =
    import.meta.env.VITE_SHOW_ONLY_PRODUCTION_MODULES === 'true'

  return (
    !showOnlyProductionModules ||
    REPORT_SECTION_AVAILABILITY[section].production
  )
}

interface ActiveReportContext {
  report: string
  reportName: string
  filters: Record<string, unknown>
  canExport: boolean
  ready: boolean
  download: (limit?: number) => Promise<{ blob: Blob; filename: string }>
}

interface FrozenEmailReportContext
  extends Omit<ActiveReportContext, 'download'> {
  idempotencyKey: string
}

const SALES_REPORT_NAMES: Record<SalesReportType, string> = {
  general: 'Ventas generales',
  'department-category': 'Ventas por departamento y categoría',
  products: 'Ventas por producto',
  profits: 'Utilidades de ventas',
  users: 'Ventas por usuario',
  sellers: 'Ventas por vendedor',
  customers: 'Ventas por cliente',
}

const INVENTORY_REPORT_NAMES: Record<InventoryReportType, string> = {
  stocks: 'Existencias',
  'min-max': 'Mínimos y máximos',
  cofepris: 'Bitácora COFEPRIS',
  batches: 'Lotes',
  kardex: 'Kardex de productos',
}

const CASH_REGISTER_REPORT_NAMES: Record<CashRegisterReportType, string> = {
  balances: 'Saldos de caja',
  closures: 'Cortes de caja',
}

const ORDER_REPORT_NAMES: Record<OrderReportType, string> = {
  general: 'Pedidos',
  'delivery-users': 'Pedidos por repartidor',
}

const PURCHASE_REPORT_NAMES: Record<PurchaseReportTab, string> = {
  general: 'Compras generales',
  products: 'Compras por producto',
  suppliers: 'Compras por proveedor',
  'accounts-payable-report': 'Cuentas por pagar',
}

const CFDI_REPORT_NAMES: Record<CfdiReportType, string> = {
  invoices: 'Facturas (impuestos)',
  'credit-notes': 'Notas de crédito',
}

const TERMINAL_EMAIL_STATUSES = new Set([
  'sent',
  'failed',
  'delivery_unknown',
])

function notifyEmailResult(
  request: ReportEmailRequestResult,
  reportName: string,
) {
  if (request.status === 'sent') {
    toast.success(`${reportName} fue aceptado por el servidor de correo.`)
    return
  }
  if (request.status === 'delivery_unknown') {
    toast.warning(
      `No fue posible confirmar la entrega de ${reportName}. No se reenviará automáticamente.`,
    )
    return
  }
  toast.error(
    request.error_message || `No fue posible enviar ${reportName}.`,
  )
}

function createIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))

  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
}

export function Reports() {
  const canSalesView = useSessionStore((state) =>
    state.canAction('reports.sales.view'),
  )
  const canInventoryView = useSessionStore((state) =>
    state.canAction('reports.inventory.view'),
  )
  const canCashMovementsView = useSessionStore((state) =>
    state.canAction('reports.cash-movements.view'),
  )
  const canCashRegistersView = useSessionStore((state) =>
    state.canAction('reports.cash-registers.view'),
  )
  const canSaleReturnsView = useSessionStore((state) =>
    state.canAction('reports.sale-returns.view'),
  )
  const canOrdersView = useSessionStore((state) =>
    state.canAction('reports.orders.view'),
  )
  const canPurchasesView = useSessionStore((state) =>
    state.canAction('reports.purchases.view'),
  )
  const canCfdiView = useSessionStore((state) =>
    state.canAction('reports.cfdi.view'),
  )
  const canSalesExport = useSessionStore((state) =>
    state.canAction('reports.sales.export'),
  )
  const canInventoryExport = useSessionStore((state) =>
    state.canAction('reports.inventory.export'),
  )
  const canCashMovementsExport = useSessionStore((state) =>
    state.canAction('reports.cash-movements.export'),
  )
  const canCashRegistersExport = useSessionStore((state) =>
    state.canAction('reports.cash-registers.export'),
  )
  const canSaleReturnsExport = useSessionStore((state) =>
    state.canAction('reports.sale-returns.export'),
  )
  const canOrdersExport = useSessionStore((state) =>
    state.canAction('reports.orders.export'),
  )
  const canPurchasesExport = useSessionStore((state) =>
    state.canAction('reports.purchases.export'),
  )
  const canCfdiExport = useSessionStore((state) =>
    state.canAction('reports.cfdi.export'),
  )
  const canEmailSend = useSessionStore((state) =>
    state.canAction('reports.email.send'),
  )
  const showReturns = isReportSectionVisible('devoluciones')
  const showOrders = isReportSectionVisible('pedidos')
  const showCfdi = isReportSectionVisible('cfdi')
  const showPurchases = isReportSectionVisible('compras')
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isRegisteringEmail, setIsRegisteringEmail] = useState(false)
  const isRegisteringEmailRef = useRef(false)
  const [emailContext, setEmailContext] =
    useState<FrozenEmailReportContext | null>(null)
  const [trackedEmailRequest, setTrackedEmailRequest] = useState<{
    id: string
    reportName: string
  } | null>(null)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [activeSection, setActiveSection] = useState(
    canSalesView
      ? 'ventas'
      : canSaleReturnsView
        ? 'devoluciones'
        : canOrdersView
          ? 'pedidos'
          : showCfdi && canCfdiView
            ? 'cfdi'
            : canPurchasesView
              ? 'compras'
              : canInventoryView
                ? 'inventario'
                : canCashMovementsView
                  ? 'movimientos'
                  : 'cajas',
  )
  const [activeSalesReport, setActiveSalesReport] =
    useState<SalesReportType>('general')
  const [activeInventoryReport, setActiveInventoryReport] =
    useState<InventoryReportType>('stocks')
  const [activeCashRegisterReport, setActiveCashRegisterReport] =
    useState<CashRegisterReportType>('balances')
  const [activeOrderReport, setActiveOrderReport] =
    useState<OrderReportType>('general')
  const [activeCfdiReport, setActiveCfdiReport] =
    useState<CfdiReportType>('invoices')
  const [activePurchaseReport, setActivePurchaseReport] =
    useState<PurchaseReportTab>('general')
  const [accountsPayableContext, setAccountsPayableContext] = useState<AccountsPayableReportContext | null>(null)
  const isAccountsPayableReport =
    activeSection === 'compras' &&
    activePurchaseReport === 'accounts-payable-report'
  const [appliedSalesContext, setAppliedSalesContext] =
    useState<AppliedSalesReportContext>({
      type: 'general',
      filters: { with_taxes: true },
    })
  const [appliedInventoryContext, setAppliedInventoryContext] =
    useState<AppliedInventoryReportContext>({
      type: 'stocks',
      filters: { stock_status: 'all', show_utilities: false },
    })
  const [appliedCashMovementContext, setAppliedCashMovementContext] =
    useState<AppliedCashMovementReportContext>({
      filters: { direction: 'all', movement: 'all' },
    })
  const [appliedCashRegisterContext, setAppliedCashRegisterContext] =
    useState<AppliedCashRegisterReportContext>({
      type: 'balances',
      filters: {},
    })
  const [appliedSaleReturnContext, setAppliedSaleReturnContext] =
    useState<AppliedSaleReturnReportContext>({
      filters: { status: 'all' },
    })
  const [appliedOrderContext, setAppliedOrderContext] =
    useState<AppliedOrderReportContext>({
      type: 'general',
      filters: {},
    })
  const [appliedPurchaseContext, setAppliedPurchaseContext] =
    useState<AppliedPurchaseReportContext>({
      type: 'general',
      filters: {},
    })
  const [appliedCfdiContext, setAppliedCfdiContext] =
    useState<AppliedCfdiReportContext>({
      type: 'invoices',
      filters: { status: 'all' },
    })

  useEffect(() => {
    const canViewActive =
      (activeSection === 'ventas' && canSalesView) ||
      (activeSection === 'inventario' && canInventoryView) ||
      (activeSection === 'movimientos' && canCashMovementsView) ||
      (activeSection === 'cajas' && canCashRegistersView) ||
      (activeSection === 'devoluciones' && showReturns && canSaleReturnsView) ||
      (activeSection === 'pedidos' && showOrders && canOrdersView) ||
      (activeSection === 'cfdi' && showCfdi && canCfdiView) ||
      (activeSection === 'compras' && showPurchases && canPurchasesView) ||
      ![
        'ventas',
        'inventario',
        'movimientos',
        'cajas',
        'devoluciones',
        'pedidos',
        'cfdi',
        'compras',
      ].includes(activeSection)
    if (!canViewActive) {
      setActiveSection(
        canSalesView
          ? 'ventas'
          : canSaleReturnsView
            ? 'devoluciones'
            : canOrdersView
              ? 'pedidos'
              : showCfdi && canCfdiView
                ? 'cfdi'
                : canPurchasesView
                  ? 'compras'
                  : canInventoryView
                    ? 'inventario'
                    : canCashMovementsView
                      ? 'movimientos'
                      : 'cajas',
      )
    }
  }, [
    activeSection,
    canCashMovementsView,
    canCashRegistersView,
    canCfdiView,
    canInventoryView,
    canOrdersView,
    canPurchasesView,
    canSaleReturnsView,
    canSalesView,
    showReturns,
    showCfdi,
    showOrders,
    showPurchases,
  ])

  const activeReportContext: ActiveReportContext | null = (() => {
    if (activeSection === 'ventas') {
      const { type, filters } = appliedSalesContext
      return {
        report: `sales.${type}`,
        reportName: SALES_REPORT_NAMES[type],
        filters: { ...filters },
        canExport: canSalesExport,
        ready: type === activeSalesReport,
        download: (limit) =>
          reportsService.exportSalesReport(type, { ...filters, limit }),
      }
    }
    if (activeSection === 'inventario') {
      const { type, filters } = appliedInventoryContext
      return {
        report: `inventory.${type}`,
        reportName: INVENTORY_REPORT_NAMES[type],
        filters: { ...filters },
        canExport: canInventoryExport,
        ready: type === activeInventoryReport,
        download: (limit) =>
          reportsService.exportInventoryReport(type, { ...filters, limit }),
      }
    }
    if (activeSection === 'movimientos') {
      const { filters } = appliedCashMovementContext
      return {
        report: 'cash-movements',
        reportName: 'Movimientos de dinero',
        filters: { ...filters },
        canExport: canCashMovementsExport,
        ready: true,
        download: (limit) =>
          reportsService.exportCashMovementsReport({ ...filters, limit }),
      }
    }
    if (activeSection === 'cajas') {
      const { type, filters } = appliedCashRegisterContext
      return {
        report: `cash-registers.${type}`,
        reportName: CASH_REGISTER_REPORT_NAMES[type],
        filters: { ...filters },
        canExport: canCashRegistersExport,
        ready: type === activeCashRegisterReport,
        download: (limit) =>
          reportsService.exportCashRegisterReport(type, { ...filters, limit }),
      }
    }
    if (activeSection === 'devoluciones') {
      const { filters } = appliedSaleReturnContext
      return {
        report: 'sale-returns',
        reportName: 'Devoluciones',
        filters: { ...filters },
        canExport: canSaleReturnsExport,
        ready: true,
        download: (limit) =>
          reportsService.exportSaleReturnReport({ ...filters, limit }),
      }
    }
    if (activeSection === 'pedidos') {
      const { type, filters } = appliedOrderContext
      return {
        report: `orders.${type}`,
        reportName: ORDER_REPORT_NAMES[type],
        filters: { ...filters },
        canExport: canOrdersExport,
        ready: type === activeOrderReport,
        download: (limit) =>
          reportsService.exportOrderReport(type, { ...filters, limit }),
      }
    }
    if (activeSection === 'cfdi') {
      const { type, filters } = appliedCfdiContext
      return {
        report: `cfdi.${type}`,
        reportName: CFDI_REPORT_NAMES[type],
        filters: { ...filters },
        canExport: canCfdiExport,
        ready: type === activeCfdiReport,
        download: (limit) =>
          reportsService.exportCfdiReport(type, { ...filters, limit }),
      }
    }
    if (activeSection === 'compras') {
      if (isAccountsPayableReport) {
        const filters = accountsPayableContext?.filters ?? {}
        return {
          report: 'purchases.accounts-payable',
          reportName: PURCHASE_REPORT_NAMES['accounts-payable-report'],
          filters: { ...filters },
          canExport: canPurchasesExport,
          ready: accountsPayableContext?.ready === true,
          download: (limit) =>
            reportsService.exportPurchaseReport('accounts-payable', {
              ...filters,
              limit,
            }),
        }
      }
      const { type, filters } = appliedPurchaseContext
      return {
        report: `purchases.${type}`,
        reportName: PURCHASE_REPORT_NAMES[type],
        filters: { ...filters },
        canExport: canPurchasesExport,
        ready: type === activePurchaseReport,
        download: (limit) =>
          reportsService.exportPurchaseReport(type, { ...filters, limit }),
      }
    }
    return null
  })()

  const openEmailModal = () => {
    if (!activeReportContext?.canExport || !activeReportContext.ready) return
    setEmailContext({
      report: activeReportContext.report,
      reportName: activeReportContext.reportName,
      filters: { ...activeReportContext.filters },
      canExport: activeReportContext.canExport,
      ready: activeReportContext.ready,
      idempotencyKey: createIdempotencyKey(),
    })
    setIsEmailModalOpen(true)
  }

  const handleSendEmail = async (payload: SendEmailPayload) => {
    if (!emailContext || isRegisteringEmailRef.current) return
    const limit =
      payload.limitType === 'custom' ? Number(payload.customLimit) : undefined

    isRegisteringEmailRef.current = true
    setIsRegisteringEmail(true)
    try {
      const request = await reportsService.sendReportEmail({
        report: emailContext.report,
        filters: emailContext.filters,
        limit,
        recipient_ids: payload.recipientIds,
        subject: payload.subject,
        description: payload.description,
        idempotency_key: emailContext.idempotencyKey,
      })
      setTrackedEmailRequest({
        id: request.id,
        reportName: emailContext.reportName,
      })
      setIsEmailModalOpen(false)
      toast.success(
        'Solicitud recibida; el reporte se enviará en segundo plano.',
      )
    } catch {
      toast.error('No fue posible registrar el envío del reporte.')
    } finally {
      isRegisteringEmailRef.current = false
      setIsRegisteringEmail(false)
    }
  }

  useEffect(() => {
    if (!trackedEmailRequest) return
    let cancelled = false

    const checkStatus = async () => {
      try {
        const request = await reportsService.getReportEmailRequest(
          trackedEmailRequest.id,
        )
        if (cancelled || !TERMINAL_EMAIL_STATUSES.has(request.status)) return
        notifyEmailResult(request, trackedEmailRequest.reportName)
        setTrackedEmailRequest(null)
      } catch {
        // El siguiente ciclo vuelve a intentar mientras la vista permanezca abierta.
      }
    }

    const interval = window.setInterval(checkStatus, 5000)
    void checkStatus()

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [trackedEmailRequest])

  const handleAppliedContextChange = useCallback(
    (context: AppliedSalesReportContext) => {
      setAppliedSalesContext(context)
    },
    [],
  )

  const handleAppliedInventoryContextChange = useCallback(
    (context: AppliedInventoryReportContext) => {
      setAppliedInventoryContext(context)
    },
    [],
  )

  const handleAppliedCashMovementContextChange = useCallback(
    (context: AppliedCashMovementReportContext) => {
      setAppliedCashMovementContext(context)
    },
    [],
  )

  const handleAppliedCashRegisterContextChange = useCallback(
    (context: AppliedCashRegisterReportContext) => {
      setAppliedCashRegisterContext(context)
    },
    [],
  )

  const handleAppliedSaleReturnContextChange = useCallback(
    (context: AppliedSaleReturnReportContext) => {
      setAppliedSaleReturnContext(context)
    },
    [],
  )

  const handleAppliedOrderContextChange = useCallback(
    (context: AppliedOrderReportContext) => {
      setAppliedOrderContext(context)
    },
    [],
  )

  const handleAppliedPurchaseContextChange = useCallback(
    (context: AppliedPurchaseReportContext) => {
      setAppliedPurchaseContext(context)
    },
    [],
  )

  const handleAppliedCfdiContextChange = useCallback(
    (context: AppliedCfdiReportContext) => {
      setAppliedCfdiContext(context)
    },
    [],
  )

  const handleExport = async (
    _format: ExportFormat,
    exportFilters?: ExportFilters,
  ) => {
    if (
      isExporting ||
      !activeReportContext?.canExport ||
      !activeReportContext.ready
    )
      return

    const limit =
      exportFilters?.limitType === 'custom'
        ? Number(exportFilters.customLimit)
        : undefined

    if (
      exportFilters?.limitType === 'custom' &&
      (!Number.isInteger(limit) || Number(limit) < 1)
    ) {
      toast.error('Ingresa una cantidad de registros válida.')
      return
    }

    setIsExporting(true)
    try {
      const exported = await activeReportContext.download(limit)
      const { blob, filename } = exported
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      setIsExportModalOpen(false)
      toast.success('Reporte exportado correctamente.')
    } catch {
      toast.error('No fue posible exportar el reporte.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Reportes</h1>
        <div className="flex gap-2">
          {canEmailSend && activeReportContext?.canExport && (
            <Button
              type="button"
              variant="outline"
              disabled={
                isRegisteringEmail || activeReportContext.ready === false
              }
              onClick={openEmailModal}
            >
              <Mail className="mr-2 h-4 w-4" />
              Enviar por Email
            </Button>
          )}
          {(canSalesExport ||
            canInventoryExport ||
            canCashMovementsExport ||
            canCashRegistersExport ||
            canSaleReturnsExport ||
            canOrdersExport ||
            canPurchasesExport ||
            canCfdiExport) && (
            <Button
              type="button"
              variant="outline"
              disabled={
                isExporting ||
                !activeReportContext?.canExport ||
                !activeReportContext.ready
              }
              onClick={() => setIsExportModalOpen(true)}
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar a Excel
            </Button>
          )}
        </div>
      </div>

      <Tabs
        value={activeSection}
        onValueChange={setActiveSection}
        className="space-y-6"
      >
        <TabsList>
          {canSalesView && <TabsTrigger value="ventas">Ventas</TabsTrigger>}
          {showReturns && canSaleReturnsView && (
            <TabsTrigger value="devoluciones">Devoluciones</TabsTrigger>
          )}
          {showOrders && canOrdersView && (
            <TabsTrigger value="pedidos">Pedidos</TabsTrigger>
          )}
          {showCfdi && canCfdiView && (
            <TabsTrigger value="cfdi">CFDI</TabsTrigger>
          )}
          {showPurchases && canPurchasesView && (
            <TabsTrigger value="compras">Compras</TabsTrigger>
          )}
          {canCashMovementsView && (
            <TabsTrigger value="movimientos">Movimientos de Dinero</TabsTrigger>
          )}
          {canInventoryView && (
            <TabsTrigger value="inventario">Inventario</TabsTrigger>
          )}
          {canCashRegistersView && (
            <TabsTrigger value="cajas">Cajas de Cobro</TabsTrigger>
          )}
        </TabsList>

        {canSalesView && (
          <VentasTab
            activeReport={activeSalesReport}
            onActiveReportChange={setActiveSalesReport}
            onAppliedContextChange={handleAppliedContextChange}
          />
        )}
        {showReturns && canSaleReturnsView && (
          <DevolucionesTab
            onAppliedContextChange={handleAppliedSaleReturnContextChange}
          />
        )}
        {showOrders && canOrdersView && (
          <PedidosTab
            activeReport={activeOrderReport}
            onActiveReportChange={setActiveOrderReport}
            onAppliedContextChange={handleAppliedOrderContextChange}
          />
        )}
        {showCfdi && canCfdiView && (
          <CfdiTab
            activeReport={activeCfdiReport}
            onActiveReportChange={setActiveCfdiReport}
            onAppliedContextChange={handleAppliedCfdiContextChange}
          />
        )}
        {showPurchases && canPurchasesView && (
          <ComprasTab
            onAccountsPayableContextChange={setAccountsPayableContext}
            activeReport={activePurchaseReport}
            onActiveReportChange={setActivePurchaseReport}
            onAppliedContextChange={handleAppliedPurchaseContextChange}
          />
        )}
        {canCashMovementsView && (
          <MovimientosTab
            onAppliedContextChange={handleAppliedCashMovementContextChange}
          />
        )}
        {canInventoryView && (
          <InventarioTab
            activeReport={activeInventoryReport}
            onActiveReportChange={setActiveInventoryReport}
            onAppliedContextChange={handleAppliedInventoryContextChange}
          />
        )}
        {canCashRegistersView && (
          <CajasTab
            activeReport={activeCashRegisterReport}
            onActiveReportChange={setActiveCashRegisterReport}
            onAppliedContextChange={handleAppliedCashRegisterContextChange}
          />
        )}
      </Tabs>

      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          if (!isRegisteringEmail) setIsEmailModalOpen(false)
        }}
        onSend={handleSendEmail}
        title="Enviar reporte por Email"
        reportName={emailContext?.reportName}
        isSending={isRegisteringEmail}
        loadRecipients={reportsService.getReportEmailRecipients}
      />

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        allowedFormats={['excel']}
        title="Exportar Reporte"
        showRecordLimit
        isExporting={isExporting}
      />
    </div>
  )
}
